import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type {
  AtterbergGeneral,
  AtterbergLLMethod,
  AtterbergDetail,
} from '../../../../types/atterberg';
import {
  computeLLMultipoint,
  computeTrial,
  applyOverrides,
  computeAtterbergResult,
} from '../computations';

interface Props {
  general: AtterbergGeneral;
  llTrials: AtterbergDetail[];
  plTrials: AtterbergDetail[];
}

export const FlowCurveChart = ({ general, llTrials, plTrials }: Props) => {
  const theme = useTheme();
  const method: AtterbergLLMethod = general.llMethod ?? 'Multipoint';

  const options = useMemo((): Highcharts.Options => {
    const isMultipoint = method === 'Multipoint';

    // ASTM D4318 reference-blows value. The previous attempt at this
    // ticket tried `type: 'logarithmic'` + a `tickPositioner` returning
    // raw data values [10, 20, 25, …, 100] — but on a Highcharts log
    // axis the positioner must return values in log10-space, not data-
    // space. Returning data values silently fell back to ONE bogus tick
    // and rendered zero labels (which the user then reported as
    // "scale spacing doesn't look correct"). Switching to a linear axis
    // with evenly-spaced ticks makes the scale read normally and dodges
    // the log-space gotcha entirely; the regression series is drawn as
    // a 25-point sampled curve below so the log-shape of
    // `WC = slope * log10(N) + intercept` is still visually correct.
    const REF_BLOWS = 25;
    const X_TICKS = [10, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100];

    // Trial scatter points — only include trials with both WC and N
    const points: { x: number; y: number; trial: number }[] = [];
    for (const t of llTrials) {
      const c = computeTrial(t, { method });
      if (c.waterContent !== null && t.numberOfBlows !== null && t.numberOfBlows > 0) {
        points.push({ x: t.numberOfBlows, y: c.waterContent, trial: t.trialNumber });
      }
    }

    // Final LL point (override-aware) — for the N=25 reference marker
    const result = computeAtterbergResult({
      llTrials, plTrials, method,
      isNonPlastic: general.isNonPlastic ?? false,
    });
    const finalLL = applyOverrides(general, result).ll;

    // Multipoint regression — clipped to the leftmost/rightmost trial
    // points. Drawn as a SAMPLED CURVE (25 segments) rather than two
    // endpoints connected by a chord, so `WC = slope·log10(N) + b`
    // renders as its true gentle log-shape on the linear x-axis.
    const reg = isMultipoint ? computeLLMultipoint(llTrials) : null;
    const regSeries: Highcharts.SeriesOptionsType[] = [];
    if (reg && points.length >= 2) {
      const blowsXs = points.map((p) => p.x);
      const minX = Math.min(...blowsXs);
      const maxX = Math.max(...blowsXs);
      const STEPS = 25;
      const curveData: [number, number][] = [];
      for (let i = 0; i <= STEPS; i++) {
        const x = minX + ((maxX - minX) * i) / STEPS;
        curveData.push([x, reg.slope * Math.log10(x) + reg.intercept]);
      }
      regSeries.push({
        type: 'line',
        name: 'Regression',
        data: curveData,
        color: theme.palette.primary.main,
        lineWidth: 2.5,
        marker: { enabled: false },
        enableMouseTracking: false,
      });
    }

    // Y-axis: 5-unit rounded padding (PDF convention) so the curve isn't
    // squeezed into a 3-unit band. For values ~34–37 this yields 25–45
    // with major gridlines every 5 — exactly the BGC-reference layout.
    const yVals = points.map((p) => p.y);
    if (finalLL !== null) yVals.push(finalLL);
    const dataMin = yVals.length ? Math.min(...yVals) : 25;
    const dataMax = yVals.length ? Math.max(...yVals) : 45;
    const yMin = Math.max(0, Math.floor((dataMin - 5) / 5) * 5);
    const yMax = Math.ceil((dataMax + 5) / 5) * 5;

    // L-shaped LL indicator. Replaces the old vertical+horizontal
    // plotLines + rotated-label-anchored-to-top combo (whose label
    // bled into the data area and is what the user described as
    // "skewed overlay text"). The horizontal dash runs y-axis → N=25;
    // the vertical dash runs x-axis → (25, LL). The "LL = XX" callout
    // anchors at the top of the vertical segment (point (25, LL)),
    // rotated -90° with a tight offset so it lives beside the
    // L-corner, never on the curve.
    const indicatorSeries: Highcharts.SeriesOptionsType[] = [];
    if (finalLL !== null && isMultipoint) {
      const indicatorColor = alpha(theme.palette.error.main, 0.7);
      indicatorSeries.push({
        type: 'line',
        name: '',
        data: [[10, finalLL], [REF_BLOWS, finalLL]],
        color: indicatorColor,
        lineWidth: 1,
        dashStyle: 'Dash',
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
        zIndex: 4,
      });
      indicatorSeries.push({
        type: 'line',
        name: '',
        data: [
          [REF_BLOWS, yMin],
          {
            x: REF_BLOWS, y: finalLL,
            dataLabels: {
              enabled: true,
              format: `LL = ${finalLL}`,
              rotation: -90,
              align: 'left', verticalAlign: 'middle',
              x: 8, y: -34, crop: false, overflow: 'allow',
              style: {
                color: theme.palette.error.main,
                fontSize: '11px',
                fontWeight: '600',
                textOutline: '2px white',
              },
            },
          } as Highcharts.PointOptionsObject,
        ],
        color: indicatorColor,
        lineWidth: 1,
        dashStyle: 'Dash',
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
        zIndex: 4,
      });
    }

    // Subtitle: regression equation + R² (kept from the previous fix —
    // user didn't complain about this and it answers their original
    // "show me the regression on the chart" ask).
    const subtitleText = reg
      ? `WC = ${reg.slope.toFixed(3)} · log<sub>10</sub>(N) + ${reg.intercept.toFixed(3)}` +
        `   ·   R² = ${reg.r2.toFixed(4)}`
      : '';

    return {
      chart: {
        height: 380,
        backgroundColor: 'transparent',
        spacing: [12, 12, 12, 12],
      },
      title: {
        text: 'Flow Curve — Water Content vs. Number of Blows',
        style: { fontSize: '13px', fontWeight: '600' },
      },
      subtitle: reg ? {
        text: subtitleText,
        useHTML: true,
        style: {
          fontSize: '11px',
          color: theme.palette.text.secondary,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        },
      } : undefined,
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        type: 'linear',
        min: 10, max: 100,
        title: { text: 'No. of Blows (N)', style: { fontSize: '11px' } },
        // Linear axis — every tick gets a label so the reader can see
        // exactly where each gridline sits. The REF_BLOWS (25) tick is
        // rendered in red as the LL reference; everything else uses the
        // default label colour.
        tickPositioner: function () { return X_TICKS; },
        gridLineWidth: 1,
        labels: {
          useHTML: true,
          formatter: function (this: { value: number | string }) {
            const v = typeof this.value === 'number' ? this.value : Number(this.value);
            if (v === REF_BLOWS) {
              return `<span style="color:${theme.palette.error.main};font-weight:600">${v}</span>`;
            }
            return String(v);
          },
          style: { fontSize: '10px' },
        },
      },
      yAxis: {
        min: yMin, max: yMax,
        tickInterval: 5,
        title: { text: 'Water Content (%)', style: { fontSize: '11px' } },
        gridLineWidth: 1,
        labels: { style: { fontSize: '10px' } },
      },
      tooltip: {
        useHTML: true,
        formatter(this: Highcharts.Point) {
          const trial = (this as unknown as { trial?: number }).trial;
          const yStr = typeof this.y === 'number' ? this.y.toFixed(2) : '—';
          if (trial !== undefined) {
            return `<b>Trial ${trial}</b><br/>N = ${this.x}<br/>WC = ${yStr}%`;
          }
          return `N = ${this.x}<br/>WC = ${yStr}%`;
        },
      },
      series: [
        ...regSeries,
        ...indicatorSeries,
        {
          type: 'scatter',
          name: 'Trials',
          data: points,
          color: theme.palette.primary.main,
          marker: {
            radius: 6,
            symbol: 'circle',
            fillColor: 'transparent',
            lineColor: theme.palette.primary.main,
            lineWidth: 1.75,
          },
          zIndex: 5,
        },
      ],
    };
  }, [llTrials, plTrials, general, method, theme.palette]);

  return (
    <Paper elevation={0} sx={{
      p: 1.5, borderRadius: 2,
      border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
    }}>
      <HighchartsReact highcharts={Highcharts} options={options} />
      {method === 'SinglePoint' && (
        <Box sx={{ textAlign: 'center', mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Single-point method — regression line not applicable
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
